import { CarouselGalleryUI } from "./CarouselGalleryUI";
import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import { isAvailableToOrder } from "@/utils/product/isAvailableToOrder";
import {
  hasSocialProofMessages,
  videoPreloadValues,
} from "@/components/Galleries/utils";
import { homeAccesoriesImageTestMapping } from "@/components/Galleries/CarouselGallery/experience/mapping.ts";
import get from "lodash/get";
import { isM2MRomanBlindOrCurtain } from "@/utils/product/isMadeToMeasure";
import { useRouter } from "next/router";
import { filterM2MImages } from "./util/filterM2MImages";
import { parseFlagEnumValue } from "@/utils/flags/parseFlagEnumValue.ts";
import { GalleryContextProvider } from "@/components/Galleries/context/GalleryContext";

export function CarouselGallery({
  sizes,
  ...props
}: Pick<
  StandardProps,
  "product" | "flags" | "experiencesRawObject" | "runtimeConfig" | "configProps"
> & {
  sizes: string;
}) {
  const { product, configProps, flags, experiencesRawObject, runtimeConfig } =
    props;
  const router = useRouter();
  const { variant } = useVariant(product);
  const media = "media" in product ? product.media : null;
  const videos = media?.videos || [];
  const showSocialProofMessages = flags.showSocialProofMessages.value === true;
  const spm = hasSocialProofMessages(configProps)
    ? configProps.SocialProofMessages
    : undefined;
  const socialProofMessagesProperties =
    product.__typename === "SimpleProduct" && spm
      ? {
          appId: spm.appId ?? "",
          withClose: spm.withClose,
          productId: product.aliases.pimProductId,
          brand: product.brand.name,
          availableToOrder: isAvailableToOrder(product),
          experiencesRawObject,
          showSocialProofMessages,
          monetateEnvironment: runtimeConfig.monetateEnvironment,
          reduceSocialProofMessagesPersistence:
            flags.reduceSocialProofMessagesPersistence.value === true,
        }
      : undefined;
  const condensedGallery =
    configProps?.condensedGallery === true ||
    flags.condensedGallery.value === true;

  const videoPreload = parseFlagEnumValue(
    String(flags.videoPreload?.value),
    videoPreloadValues,
    "auto",
  );

  const amendCarouselZoomIcon = flags.amendCarouselZoomIcon?.value;

  if (!variant) return null;

  let homeAccessoriesTestImages;

  if (flags.homeAccessoriesImageTest.value) {
    const replacementImages = get(homeAccesoriesImageTestMapping, [
      product.aliases.pimProductId,
      variant.displayCode,
    ]);

    if (replacementImages) {
      const [primary = "", ...alternatives] = replacementImages;
      homeAccessoriesTestImages = { primary, alternatives };
    }
  }

  let images = variant.images;
  if (isM2MRomanBlindOrCurtain(product)) {
    images = filterM2MImages(images, router.query);
  }

  return (
    <GalleryContextProvider {...props}>
      <CarouselGalleryUI
        title={variant.title}
        images={homeAccessoriesTestImages || images}
        videos={videos}
        sizes={sizes}
        hasSocialProofMessagesExperience={true}
        hasImmediateStateUpdates={!!flags.hasImmediateStateUpdates.value}
        socialProofMessagesProperties={socialProofMessagesProperties}
        hasTransparentThumbnails={
          configProps?.hasTransparentThumbnails === true
        }
        hasDeferredMonetateQEvents={!!flags.hasDeferredMonetateQEvents.value}
        hasDeferredIntersectionObserverCallback={
          !!flags.hasDeferredIntersectionObserverCallback.value
        }
        hasSuperZoom={!flags.isSuperZoomDisabled.value}
        dataComponent="CarouselGallery"
        useFetchPriority={!!flags.usePrefetching.value}
        useResponsiveImages={!!flags.useResponsiveImages.value}
        carouselGalleryInpTest={flags.carouselGalleryInpTest.value === true}
        videoPreload={videoPreload}
        condensedGallery={condensedGallery}
        experiencesRawObject={experiencesRawObject}
        runtimeConfig={runtimeConfig}
        amendCarouselZoomIcon={amendCarouselZoomIcon}
      />
    </GalleryContextProvider>
  );
}
